import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { secretService } from '@/services/secret-service';
import type { Secret } from '@/types/core/secret';
import type { JSONObject } from '@/types/shared/json-schema';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretMutationKeys, secretQueryKeys } from './query-keys';

/**
 * Hook to update a secret's value.
 * Waits until the read model is updated (find returns), then invalidates the cache.
 */
export function useUpdateSecretValue(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation({
    mutationKey: secretMutationKeys.updateSecretValue(tenantId),
    mutationFn: async (payload: { secretId: UUID; raw: JSONObject }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const cachedSecret = qc.getQueryData<Secret | null>(
        secretQueryKeys.detail(tenantId, payload.secretId)
      );
      const minRev = cachedSecret?._rev ?? -2;

      await secretService.updateSecretValue(
        tenantId,
        payload.secretId,
        payload.raw
      );

      await pollUntilResourceNewerThan(
        () => secretService.findSecret(tenantId, payload.secretId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );

      return payload;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: secretQueryKeys.list(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: secretQueryKeys.detail(tenantId, variables.secretId),
      });
    },
  });
}
